import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';
import { PageHeader } from '@/components/shell/page-header';
import { overviewQuery, walletsQuery } from '@/features/admin/admin-api';
import { AdminScreen, type AdminView } from '@/features/admin/admin-screen';
import { meQuery } from '@/lib/queries';

export const Route = createFileRoute('/_app/admin')({
  // The console uses the whole screen width beside the sidebar.
  staticData: { fullWidth: true },
  validateSearch: z.object({
    /** Users, Wallets or Platform; Overview is the default and stays out of the URL. */
    view: z.enum(['users', 'wallets', 'platform']).optional().catch(undefined),
    /** The account whose sheet is open (over any view). */
    user: z
      .string()
      .regex(/^[a-f0-9]{24}$/)
      .optional()
      .catch(undefined),
  }),
  // Admins only: everyone else lands on the overview (the API refuses them anyway).
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me.user.role !== 'admin') throw redirect({ to: '/', replace: true });
  },
  loaderDeps: ({ search }) => ({ view: search.view ?? 'overview' }),
  loader: ({ context: { queryClient }, deps }) => {
    // Start the view's first request with the route, not after its first render.
    if (deps.view === 'overview') void queryClient.prefetchQuery(overviewQuery(30));
    if (deps.view === 'wallets') void queryClient.prefetchInfiniteQuery(walletsQuery('', 'active'));
  },
  component: AdminRoute,
});

function AdminRoute() {
  const { view = 'overview', user } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const { data: me } = useSuspenseQuery(meQuery);
  // Opening a sheet pushes a history entry, so Back closes it; closing it from inside steps back over
  // that entry. A sheet opened from a link or a reload has no entry of ours behind it: replace.
  const pushed = useRef(false);
  useEffect(() => {
    if (!user) pushed.current = false;
  }, [user]);

  const onOpen = useCallback(
    (id: string) => {
      const replace = pushed.current;
      pushed.current = true;
      void navigate({ search: (prev) => ({ ...prev, user: id }), replace });
    },
    [navigate],
  );
  const onClose = useCallback(() => {
    if (pushed.current) {
      pushed.current = false;
      router.history.back();
    } else {
      void navigate({ search: (prev) => ({ ...prev, user: undefined }), replace: true });
    }
  }, [navigate, router]);

  // A view is a place: switching pushes a history entry, so Back returns to the previous view.
  const onView = useCallback(
    (next: AdminView) =>
      void navigate({ search: (prev) => ({ ...prev, view: next === 'overview' ? undefined : next }) }),
    [navigate],
  );

  return (
    <>
      <PageHeader title="Admin" />
      <AdminScreen
        view={view}
        onView={onView}
        selfId={me.user.id}
        selectedId={user}
        onOpen={onOpen}
        onClose={onClose}
      />
    </>
  );
}
